/**
 * Utility per il componente TurnstileWidget.
 *
 * - mapI18nToTurnstileLang: mappa i language code i18next (es. 'it_IT') sui codici
 *   supportati dal parametro `language` del widget Turnstile (es. 'it').
 * - loadTurnstileScript: carica lo script ufficiale Cloudflare Turnstile una sola
 *   volta per pagina, deduplicando richieste concorrenti. Async/defer per non
 *   bloccare il rendering. Ritorna una Promise che risolve quando window.turnstile
 *   è disponibile.
 */

// Mappa i18next language codes -> Turnstile language codes
// Turnstile supporta: ar, cs, da, de, en, es, fa, fi, fr, he, hi, hr, hu, id, it,
//                     ja, ko, ms, nl, no, pl, pt, ro, ru, sk, sl, sr, sv, th, tlh,
//                     tr, uk, vi, zh, zh-CN, zh-TW
export function mapI18nToTurnstileLang(i18nLang) {
  if (!i18nLang) return 'auto'
  const lower = String(i18nLang).toLowerCase()
  // Prima gestione dei code composti (zh_CN, pt_BR, ecc.)
  if (lower.startsWith('zh')) {
    if (lower.includes('tw')) return 'zh-TW'
    return 'zh-CN'
  }
  if (lower.startsWith('pt')) return 'pt' // pt_PT o pt_BR → pt
  if (lower.startsWith('en')) return 'en'
  // Fallback: primi 2 char
  const short = lower.slice(0, 2)
  const supported = ['ar','cs','da','de','en','es','fa','fi','fr','he','hi','hr','hu','id','it','ja','ko','ms','nl','no','pl','pt','ro','ru','sk','sl','sr','sv','th','tr','uk','vi']
  return supported.includes(short) ? short : 'auto'
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
let scriptPromise = null

/**
 * Carica lo script Turnstile. Singleton: chiamate concorrenti condividono la
 * stessa Promise. Rifiuta se lo script non si carica entro 10s.
 */
export function loadTurnstileScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('turnstile_no_window'))
  }
  if (window.turnstile) {
    return Promise.resolve(window.turnstile)
  }
  if (scriptPromise) {
    return scriptPromise
  }
  scriptPromise = new Promise((resolve, reject) => {
    // Se lo script è già stato inserito ma non ha finito di caricare
    const existing = document.querySelector(`script[src="${SCRIPT_URL}"]`)
    if (existing) {
      existing.addEventListener('load', () => {
        window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile_not_available'))
      })
      existing.addEventListener('error', () => reject(new Error('turnstile_script_error')))
      return
    }
    const s = document.createElement('script')
    s.src = SCRIPT_URL
    s.async = true
    s.defer = true
    s.onload = () => {
      window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile_not_available'))
    }
    s.onerror = () => {
      scriptPromise = null // permetti retry
      reject(new Error('turnstile_script_error'))
    }
    document.head.appendChild(s)
    // Safety timeout
    setTimeout(() => {
      if (!window.turnstile) {
        scriptPromise = null
        reject(new Error('turnstile_script_timeout'))
      }
    }, 10000)
  })
  return scriptPromise
}
